import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixAcademyCertificationsV1UserCertification from './academy-certifications-v1-user-certification-user-certifications.http.js';
// @ts-ignore

/**
 * A user certification represents a Wix Academy certification held by an individual user,
 * such as the Velo, Web Designer, or Wix Studio Developer certification.
 *
 * A user earns a certification by passing the corresponding Wix Academy exam or course.
 * Once earned, a certification is permanent and remains valid until it's removed administratively,
 * so you can treat the presence of a user certification as currently valid.
 * A user holds each certification type at most once.
 *
 * To read certifications at the account level, see the Account Certifications API.
 */
export interface UserCertification {
  /**
   * User certification ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the user certification is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the user certification.
   *
   * Ignored when creating a user certification.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the user certification was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the user certification was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * ID of the certified user. Set when the certification is created and can't be changed afterward.
   * @format GUID
   * @immutable
   */
  userId?: string | null;
  /**
   * Type of certification the user holds. Set when the certification is created and can't be changed afterward.
   * @immutable
   */
  certificationType?: CertificationTypeWithLiterals;
}

export enum CertificationType {
  /** Velo certification: proficiency in building with Velo, Wix's full-stack development platform. */
  VELO = 'VELO',
  /** Web Designer certification: proficiency in designing and building Wix websites. */
  WEB_DESIGNER = 'WEB_DESIGNER',
  /** Accessibility certification: proficiency in building accessible Wix websites. */
  ACCESSIBILITY = 'ACCESSIBILITY',
  /** Wix Studio Developer certification: proficiency in developing with Wix Studio. */
  STUDIO_DEVELOPER = 'STUDIO_DEVELOPER',
  /** Wix Studio Design League certification, awarded through the Wix Studio Design League program. */
  STUDIO_DESIGN_LEAGUE = 'STUDIO_DESIGN_LEAGUE',
  /** Wix Studio Developer Websites League certification, awarded through the Wix Studio Developer Websites League program. */
  STUDIO_DEV_WEBSITES_LEAGUE = 'STUDIO_DEV_WEBSITES_LEAGUE',
}

/** @enumType */
export type CertificationTypeWithLiterals =
  | CertificationType
  | 'VELO'
  | 'WEB_DESIGNER'
  | 'ACCESSIBILITY'
  | 'STUDIO_DEVELOPER'
  | 'STUDIO_DESIGN_LEAGUE'
  | 'STUDIO_DEV_WEBSITES_LEAGUE';

export interface CreateUserCertificationRequest {
  /** User certification to create. */
  userCertification?: UserCertification;
}

export interface CreateUserCertificationResponse {
  /** Created user certification. */
  userCertification?: UserCertification;
}

export interface GetUserCertificationRequest {
  /**
   * ID of the user certification to retrieve.
   * @format GUID
   */
  userCertificationId: string;
}

export interface GetUserCertificationResponse {
  /** Retrieved user certification. */
  userCertification?: UserCertification;
}

export interface DeleteUserCertificationRequest {
  /**
   * ID of the user certification to delete.
   * @format GUID
   */
  userCertificationId?: string;
}

export interface DeleteUserCertificationResponse {}

export interface QueryUserCertificationsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) for more information. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 4
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 50
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryUserCertificationsResponse {
  /** List of user certifications. */
  userCertifications?: UserCertification[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor pointing to next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Retrieves a user certification.
 * @param userCertificationId - ID of the user certification to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField userCertificationId
 * @permissionId ACADEMY.USER_CERTIFICATION_READ
 * @returns Retrieved user certification.
 * @fqn wix.academy.certifications.user.v1.UserCertifications.GetUserCertification
 */
export async function getUserCertification(
  userCertificationId: string
): Promise<NonNullablePaths<UserCertification, `certificationType`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    userCertificationId: userCertificationId,
  });

  const reqOpts =
    ambassadorWixAcademyCertificationsV1UserCertification.getUserCertification(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.userCertification!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { userCertificationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['userCertificationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
